import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTrack } from '@optimizely/segment-sdk-wrapper';
import {
  Attention,
  Button,
  ButtonIcon,
  Checkbox,
  Fieldset,
  HelpPopover,
  Icon,
  Link,
  Poptip,
  Sheet,
  Spinner,
  Table,
} from '@optimizely/axiom';
import { getCurrentLoginUserEmail, getCurrentUser, truncate } from 'utils';
import SearchMultiSelectDropdown from 'app/flag_manager/components/SearchMultiSelectDropdown';
import useFlagsData from 'services/api/useFlagsData';
import useUsersData, { User, Users } from 'services/api/useUsersData';
import {
  ApprovalActionsTooltip,
  PendingRequestErrorMessage,
} from 'app/flag_manager/constants';

import useEnvironmentApprovalSettingsData from 'services/api/useEnvironmentApprovalSettingsData';
import { Flags } from 'types/backend';
import { PageableAPIResponse } from 'types/frontend';
import isEqual from 'lodash.isequal';
import _ from 'lodash';

interface SetupApprovalsDialogProps {
  closeDialog: () => void;
  entityId: string;
  entityKey: string;
  entityName: string;
  entityType: string;
  projectId: string;
}
type CheckboxId =
  | 'ruleset'
  | 'rules'
  | 'audiences'
  | 'ramp_perc'
  | 'metrics'
  | 'variations';
type checkboxType = {
  [key in CheckboxId]: string[];
};
type TableType = 'flags' | 'approvers' | 'additionalUsers';

type SelectedFlag = {
  entity_id: number;
  key: string;
  name: string;
  operation?: string;
  preferences?: object;
};

type SelectedApprover = {
  email?: string;
  key: string;
  name: string;
  operation?: string;
};

type SelectedAdditionalUser = {
  email?: string;
  key: string;
  name: string;
  operation?: string;
};

type ShowMoreEntities = {
  additionalUsers: boolean;
  approvers: boolean;
  flags: boolean;
};
type CheckboxState = {
  audiences: boolean;
  metrics: boolean;
  ramp_perc: boolean;
  rules: boolean;
  ruleset: boolean;
  variations: boolean;
};

const maxInitialEntities = 5;
const roleDisplayNames = {
  admin: 'Admin',
  edit: 'Editor',
  none: 'None',
  publish: 'Publisher',
  view: 'Viewer',
};

const transformUsersForDropdown = (
  users: Users,
  isApprovers: Boolean = false
) => {
  const currentUserEmail = getCurrentUser();
  const currentLoginUserEmail = getCurrentLoginUserEmail(); //can be diff from currentUser in case of emulating
  const filteredUsers = isApprovers
    ? users.filter(user => {
        const isCurrentUser = user.email === currentUserEmail;
        const isLoginUser = user.email === currentLoginUserEmail;
        const isAdmin = user.role === 'admin';

        // Only filter out if the user is currentUser or loginUser AND they are not an admin
        return !((isCurrentUser || isLoginUser) && !isAdmin);
      })
    : users;
  return filteredUsers.map((user: User) => {
    // If both first_name and last_name are null/empty, fall back to the email (before '@')
    const userName =
      user.first_name || user.last_name
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
        : user.email.split('@')[0];

    return {
      email: user.email,
      key: user.user_id,
      name: userName,
      role: user.role,
    };
  });
};

const SetupApprovalsDialog = ({
  projectId,
  entityType,
  entityId: environmentId,
  entityName: environmentName,
  entityKey: environmentKey,
  closeDialog,
}: SetupApprovalsDialogProps) => {
  const track = useTrack();
  const {
    environmentApprovalSettings,
    isLoadingEnvironmentApprovalSettings,
    updateEnvironmentApprovalSettings,
  } = useEnvironmentApprovalSettingsData(
    Number(projectId),
    Number(environmentId)
  );
  const { usersData, isFetchingUsers, error: usersError } = useUsersData({
    entityId: environmentId,
    entityType,
    projectId,
  });

  const initialFilter = { archived: false, environment: environmentKey };
  const {
    flagsData,
    isFetchingFlags,
    searchForFlags,
    error: flagsError,
  } = useFlagsData({
    initialFilter,
    projectId,
  });
  const [submittingSave, setSubmittingSave] = useState(false);
  const [isOpenstate, setIsOpenstate] = useState(true);
  const [approvalSettingsError, setApprovalSettingsError] = useState<string>(
    ''
  );
  const [formError, setFormError] = useState<boolean>(false);
  const initialCheckboxState = {
    audiences:
      environmentApprovalSettings?.default_preferences?.audiences ?? false,
    metrics: environmentApprovalSettings?.default_preferences?.metrics ?? false,
    ramp_perc:
      environmentApprovalSettings?.default_preferences?.ramp_perc ?? false,
    rules: environmentApprovalSettings?.default_preferences?.rules ?? false,
    ruleset: environmentApprovalSettings?.default_preferences?.ruleset ?? false,
    variations:
      environmentApprovalSettings?.default_preferences?.variations ?? false,
  };
  const [checkboxState, setCheckboxState] = useState<checkboxType>({
    audiences: [],
    metrics: [],
    ramp_perc: [],
    rules: [],
    ruleset: [],
    variations: [],
  });
  // State for the default row (which applies across all columns)
  const [defaultRowCheckboxState, setDefaultRowCheckboxState] = useState(
    initialCheckboxState
  );

  const [headerCheckboxState, setHeaderCheckboxState] = useState(
    initialCheckboxState
  );
  const [selectedFlags, setSelectedFlags] = useState<SelectedFlag[]>([]);
  const [autoAddNewlyCreatedFlags, setAutoAddNewlyCreatedFlags] = useState<
    boolean
  >(environmentApprovalSettings?.require_approval_on_new_entities ?? false);
  const [
    headerCheckboxIndeterminate,
    setHeaderCheckboxIndeterminate,
  ] = useState(initialCheckboxState);
  // State for the header checkbox Indeterminate and checked state in edit mode, reflecting preselected flags
  const calculateIndeterminateState = (key: keyof checkboxType) => {
    const allSelected =
      environmentApprovalSettings?.entities?.every(entity =>
        Object.values(entity).every(preferences => preferences[key])
      ) && defaultRowCheckboxState[key];

    const noneSelected =
      environmentApprovalSettings?.entities?.every(entity =>
        Object.values(entity).every(preferences => !preferences[key])
      ) && !defaultRowCheckboxState[key];

    return !(allSelected || noneSelected); // True if partially selected
  };
  const calculateCheckedState = (key: keyof checkboxType) => {
    return !!(
      environmentApprovalSettings?.entities?.every(entity =>
        Object.values(entity).every(preferences => preferences[key])
      ) && defaultRowCheckboxState[key]
    );
  };
  const getUserRoleAndName = (usersData: Users, email: string) => {
    const transformedUsers = transformUsersForDropdown(usersData);
    const user = transformedUsers.find(user => user.email === email);
    return {
      key: user?.key || email,
      name: user?.name || email,
      role: user?.role || '',
    };
  };

  const [isDefaultRowStateSet, setIsDefaultRowStateSet] = useState(false);
  useEffect(() => {
    if (
      !isLoadingEnvironmentApprovalSettings &&
      environmentApprovalSettings?.default_preferences
    ) {
      setDefaultRowCheckboxState({
        audiences:
          environmentApprovalSettings.default_preferences.audiences ?? false,
        metrics:
          environmentApprovalSettings.default_preferences.metrics ?? false,
        ramp_perc:
          environmentApprovalSettings.default_preferences.ramp_perc ?? false,
        rules: environmentApprovalSettings.default_preferences.rules ?? false,
        ruleset:
          environmentApprovalSettings.default_preferences.ruleset ?? false,
        variations:
          environmentApprovalSettings.default_preferences.variations ?? false,
      });
      setAutoAddNewlyCreatedFlags(
        environmentApprovalSettings.require_approval_on_new_entities ?? false
      );
      setIsDefaultRowStateSet(true);
    }
  }, [isLoadingEnvironmentApprovalSettings, environmentApprovalSettings]);

  useEffect(() => {
    if (isDefaultRowStateSet) {
      // Only run after defaultrow state is set
      if (
        environmentApprovalSettings?.entities &&
        environmentApprovalSettings?.entities.length &&
        flagsData.items.length > 0
      ) {
        setHeaderCheckboxState({
          audiences: calculateCheckedState('audiences'),
          metrics: calculateCheckedState('metrics'),
          ramp_perc: calculateCheckedState('ramp_perc'),
          rules: calculateCheckedState('rules'),
          ruleset: calculateCheckedState('ruleset'),
          variations: calculateCheckedState('variations'),
        });

        setHeaderCheckboxIndeterminate({
          audiences: calculateIndeterminateState('audiences'),
          metrics: calculateIndeterminateState('metrics'),
          ramp_perc: calculateIndeterminateState('ramp_perc'),
          rules: calculateIndeterminateState('rules'),
          ruleset: calculateIndeterminateState('ruleset'),
          variations: calculateIndeterminateState('variations'),
        });

        const flags = environmentApprovalSettings?.entities
          .flatMap(entity =>
            Object.entries(entity).map(([key, preferences]) => {
              // Find the matching flag in flagsdata by key
              const matchingFlag = flagsData.items.find(
                flag => flag.key === key
              );

              // Only return flags with valid entity_id
              if (matchingFlag?.id) {
                return {
                  entity_id: matchingFlag.id as number,
                  key, // Flag key
                  name: matchingFlag?.name ?? key, // Use the name from flagsdata, or default to the key
                  operation: 'update', // Default operation
                  preferences, // Preferences from approvalSettings
                };
              }
              return null;
            })
          )
          .filter(Boolean); // Filter out null values

        setSelectedFlags(flags);

        const updatedCheckboxState: checkboxType = {
          audiences: [],
          metrics: [],
          ramp_perc: [],
          rules: [],
          ruleset: [],
          variations: [],
        };

        environmentApprovalSettings.entities.forEach(entity => {
          Object.entries(entity).forEach(([flagName, preferences]) => {
            // Check if this flag has a valid entity_id in flagsData
            const flagHasEntityId = flagsData.items.some(
              flagdata => flagdata.key === flagName && flagdata.id !== undefined
            );

            if (!flagHasEntityId) return;

            if (preferences.audiences)
              updatedCheckboxState.audiences.push(flagName);
            if (preferences.metrics)
              updatedCheckboxState.metrics.push(flagName);
            if (preferences.ramp_perc)
              updatedCheckboxState.ramp_perc.push(flagName);
            if (preferences.rules) updatedCheckboxState.rules.push(flagName);
            if (preferences.ruleset)
              updatedCheckboxState.ruleset.push(flagName);
            if (preferences.variations)
              updatedCheckboxState.variations.push(flagName);
          });
        });

        // Sort all the arrays after adding the flag names to ensure a consistent order for comparison,
        // so we can accurately determine if there are any changes to be saved or if only the order has changed.
        updatedCheckboxState.audiences.sort();
        updatedCheckboxState.metrics.sort();
        updatedCheckboxState.ramp_perc.sort();
        updatedCheckboxState.rules.sort();
        updatedCheckboxState.ruleset.sort();
        updatedCheckboxState.variations.sort();

        setCheckboxState(updatedCheckboxState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentApprovalSettings, flagsData?.items, isDefaultRowStateSet]);

  const [selectedApprovers, setSelectedApprovers] = useState<
    SelectedApprover[]
  >([]);
  const [selectedAdditionalUsers, setSelectedAdditionalUsers] = useState<
    SelectedAdditionalUser[]
  >([]);
  const [initialDialogState, setInitialDialogState] = useState({
    autoAddNewlyCreatedFlags,
    checkboxState,
    defaultRowCheckboxState,
    selectedAdditionalUsers,
    selectedApprovers,
    selectedFlags,
  });
  // Effect to set the selectedApprovers & selectedAdditionalUsers once usersData and approvalSettings are available
  useEffect(() => {
    if (
      usersData &&
      usersData.length > 0 &&
      environmentApprovalSettings?.approvers
    ) {
      setSelectedApprovers(
        environmentApprovalSettings.approvers.map(approver => ({
          email: approver.email,
          key: getUserRoleAndName(usersData, approver.email)?.key,
          name: getUserRoleAndName(usersData, approver.email)?.name,
          role: getUserRoleAndName(usersData, approver.email)?.role,
        }))
      );
      setSelectedAdditionalUsers(
        environmentApprovalSettings.notification_recipients.map(userEmail => ({
          email: userEmail,
          key: getUserRoleAndName(usersData, userEmail)?.key,
          name: getUserRoleAndName(usersData, userEmail)?.name,
        }))
      );
    }
  }, [usersData, environmentApprovalSettings]);

  useEffect(() => {
    if (
      (environmentApprovalSettings?.approvers &&
        environmentApprovalSettings?.approvers.length &&
        !initialDialogState.selectedApprovers.length) ||
      (environmentApprovalSettings?.entities &&
        environmentApprovalSettings?.entities.length &&
        !initialDialogState.selectedFlags.length)
    ) {
      setInitialDialogState({
        autoAddNewlyCreatedFlags,
        checkboxState,
        defaultRowCheckboxState,
        selectedAdditionalUsers,
        selectedApprovers,
        selectedFlags,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentApprovalSettings, selectedApprovers, selectedFlags]);

  const hasUnsavedChanges = () => {
    return (
      !isEqual(initialDialogState.checkboxState, checkboxState) ||
      !isEqual(
        initialDialogState.defaultRowCheckboxState,
        defaultRowCheckboxState
      ) ||
      !isEqual(
        _.sortBy(initialDialogState.selectedFlags, 'entity_id'),
        _.sortBy(selectedFlags, 'entity_id')
      ) ||
      !isEqual(initialDialogState.selectedApprovers, selectedApprovers) ||
      !isEqual(
        initialDialogState.selectedAdditionalUsers,
        selectedAdditionalUsers
      ) ||
      !isEqual(
        initialDialogState.autoAddNewlyCreatedFlags,
        autoAddNewlyCreatedFlags
      )
    );
  };

  const [showMoreEntities, setShowMoreEntities] = useState<ShowMoreEntities>({
    additionalUsers: false,
    approvers: false,
    flags: false,
  });

  const toggleShowMoreEntities = (entities: any[], entityType: TableType) => {
    const remainingEntities = entities.length - maxInitialEntities;
    const newShowMoreState =
      !showMoreEntities[entityType] && remainingEntities > 0;
    setShowMoreEntities(prev => ({ ...prev, [entityType]: newShowMoreState }));
  };

  const getFormattedMetadataForTrackCall = () => {
    return {
      additionalNotificationRecipients: selectedAdditionalUsers.map(
        (additionalUser: SelectedAdditionalUser) => additionalUser.email
      ),
      approvers: selectedApprovers.map(
        (approver: SelectedApprover) => approver.email
      ),
      areChangeApprovalSettingsEmpty:
        !autoAddNewlyCreatedFlags && !selectedFlags.length,
      autoAddNewlyCreatedFlags: autoAddNewlyCreatedFlags,
      defaultPreferences: defaultRowCheckboxState,
      entities: selectedFlags,
    };
  };

  const handleSaveApprovalSettings = async () => {
    setSubmittingSave(true);
    const environment_approval_settings_create_payload = {
      approver_user_emails: selectedApprovers
        .map(approver => approver.email)
        .filter((email): email is string => email !== undefined),

      default_preferences: {
        audiences: defaultRowCheckboxState.audiences,
        metrics: defaultRowCheckboxState.metrics,
        ramp_perc: defaultRowCheckboxState.ramp_perc,
        rules: defaultRowCheckboxState.rules,
        ruleset: defaultRowCheckboxState.ruleset,
        variations: defaultRowCheckboxState.variations,
      },
      entities: selectedFlags.map(flag => ({
        entity_id: flagsData.items.find(flagdata => flagdata.key === flag.key)
          ?.id as number,
        key: flag.key,
        operation: flag.operation || 'update',
        preferences: {
          audiences: checkboxState.audiences.includes(flag.key),
          metrics: checkboxState.metrics.includes(flag.key),
          ramp_perc: checkboxState.ramp_perc.includes(flag.key),
          rules: checkboxState.rules.includes(flag.key),
          ruleset: checkboxState.ruleset.includes(flag.key),
          variations: checkboxState.variations.includes(flag.key),
        },
      })),
      entity_type: 'flag',
      notification_recipients: selectedAdditionalUsers
        .map(user => user.email)
        .filter((email): email is string => email !== undefined),
      require_approval_on_new_entities: autoAddNewlyCreatedFlags,
    };
    try {
      await updateEnvironmentApprovalSettings({
        environment_approval_settings_update: environment_approval_settings_create_payload,
        environment_id: Number(environmentId),
        project_id: Number(projectId),
      });
      setSubmittingSave(false);
      closeModal();
      track('Setup Approvals Modal Saved', getFormattedMetadataForTrackCall());
    } catch (error) {
      setSubmittingSave(false);
      let errorDetail = '';
      if (error && typeof error === 'object' && error.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorDetail = parsedError.detail || '';
        } catch {
          errorDetail = error.message; // Use the raw error message if parsing fails
        }
      }
      const ErrorMessage = `Please note, something went wrong. We can't create the approval settings right now. ${errorDetail}`;
      setApprovalSettingsError(ErrorMessage);
    }
  };
  const handleUpdateApprovalSettings = async () => {
    setSubmittingSave(true);
    // Build the update payload based on changes detected
    const environment_approval_settings_update_payload: any = {};

    // Check if approvers have changed
    if (!isEqual(initialDialogState.selectedApprovers, selectedApprovers)) {
      environment_approval_settings_update_payload.approver_user_emails = selectedApprovers.map(
        approver => approver.email
      );
    }

    // Check if default preferences have changed
    if (
      !isEqual(
        initialDialogState.defaultRowCheckboxState,
        defaultRowCheckboxState
      )
    ) {
      environment_approval_settings_update_payload.default_preferences = {
        audiences: defaultRowCheckboxState.audiences,
        metrics: defaultRowCheckboxState.metrics,
        ramp_perc: defaultRowCheckboxState.ramp_perc,
        rules: defaultRowCheckboxState.rules,
        ruleset: defaultRowCheckboxState.ruleset,
        variations: defaultRowCheckboxState.variations,
      };
    }

    // Check if selected flags/checkboxes have changed
    const updatedEntities = selectedFlags
      .filter(flag => {
        const initialFlag = initialDialogState.selectedFlags.find(
          f => f.key === flag.key
        );
        return (
          !initialFlag || !isEqual(initialFlag?.preferences, flag.preferences)
        );
      })
      .map(flag => ({
        entity_id: flagsData.items.find(flagdata => flagdata.key === flag.key)
          ?.id,
        key: flag.key,
        operation: flag.operation || 'update',
        preferences: {
          audiences: checkboxState.audiences.includes(flag.key),
          metrics: checkboxState.metrics.includes(flag.key),
          ramp_perc: checkboxState.ramp_perc.includes(flag.key),
          rules: checkboxState.rules.includes(flag.key),
          ruleset: checkboxState.ruleset.includes(flag.key),
          variations: checkboxState.variations.includes(flag.key),
        },
      }));

    const deletedEntities = initialDialogState.selectedFlags
      .filter(
        initialFlag => !selectedFlags.some(flag => flag.key === initialFlag.key)
      ) // Flags in initial state but not in selected state
      .map(flag => {
        const foundFlag = flagsData.items.find(
          flagdata => flagdata.key === flag.key
        );
        return foundFlag?.id
          ? {
              entity_id: foundFlag.id,
              key: flag.key,
              operation: 'delete',
              preferences: {
                audiences: false,
                metrics: false,
                ramp_perc: false,
                rules: false,
                ruleset: false,
                variations: false,
              },
            }
          : null;
      })
      .filter(Boolean);
    const updatedEntitiesToSave = [...updatedEntities, ...deletedEntities];
    if (updatedEntitiesToSave.length > 0) {
      environment_approval_settings_update_payload.entities = updatedEntitiesToSave;
    }

    // Check if notification recipients have changed
    if (
      !isEqual(
        initialDialogState.selectedAdditionalUsers,
        selectedAdditionalUsers
      )
    ) {
      environment_approval_settings_update_payload.notification_recipients = selectedAdditionalUsers
        .map(user => user.email)
        .filter((email): email is string => email !== undefined);
    }
    if (
      !isEqual(
        initialDialogState.autoAddNewlyCreatedFlags,
        autoAddNewlyCreatedFlags
      )
    ) {
      environment_approval_settings_update_payload.require_approval_on_new_entities = autoAddNewlyCreatedFlags;
    }

    // Proceed with the update if there are changes
    if (Object.keys(environment_approval_settings_update_payload).length > 0) {
      try {
        await updateEnvironmentApprovalSettings({
          environment_approval_settings_update: {
            ...environment_approval_settings_update_payload,
            entity_type: 'flag',
          },
          environment_id: Number(environmentId),
          project_id: Number(projectId),
        });
        setSubmittingSave(false);
        closeModal();

        track(
          'Setup Approvals Modal Saved',
          getFormattedMetadataForTrackCall()
        );
      } catch (error) {
        setSubmittingSave(false);
        let errorDetail = '';
        if (error && typeof error === 'object' && error.message) {
          try {
            const parsedError = JSON.parse(error.message);
            errorDetail = parsedError.detail || '';
          } catch {
            errorDetail = error.message; // Use the raw error message if parsing fails
          }
        }
        const ErrorMessage = errorDetail.includes(
          'There are pending change requests associated'
        )
          ? PendingRequestErrorMessage
          : `Please note, something went wrong. We can't update the approval settings right now.`;
        setApprovalSettingsError(ErrorMessage);
      }
    }
  };

  const renderShowMoreLink = (
    selectedEntities: any[],
    entityType: 'flags' | 'approvers' | 'additionalUsers'
  ) => {
    const remainingEntities = selectedEntities.length - maxInitialEntities;

    if (
      !showMoreEntities[entityType] &&
      selectedEntities.length > maxInitialEntities &&
      remainingEntities > 0
    ) {
      return (
        <Link
          onClick={() => toggleShowMoreEntities(selectedEntities, entityType)}>
          +{remainingEntities} more
        </Link>
      );
    }

    if (showMoreEntities[entityType]) {
      return (
        <Link
          onClick={() => toggleShowMoreEntities(selectedEntities, entityType)}>
          Show less
        </Link>
      );
    }

    return null;
  };

  const closeModal = () => {
    setIsOpenstate(false);
    closeDialog();
  };

  return (
    <div>
      {isOpenstate && (
        <Sheet
          footerButtonList={[
            <Button
              key={0}
              onClick={closeModal}
              style="plain"
              testSection="cancel-setup-button">
              Cancel
            </Button>,
            <Button
              isDisabled={
                !hasUnsavedChanges() ||
                isFetchingUsers ||
                isLoadingEnvironmentApprovalSettings ||
                submittingSave
              }
              isLoading={submittingSave}
              key={1}
              loadingText={'Saving...'}
              onClick={() => {
                if (
                  selectedApprovers.length === 0 &&
                  (selectedFlags.length > 0 || autoAddNewlyCreatedFlags)
                ) {
                  setFormError(true);
                } else {
                  (environmentApprovalSettings &&
                    environmentApprovalSettings.approvers.length) ||
                  (environmentApprovalSettings &&
                    environmentApprovalSettings.entities &&
                    environmentApprovalSettings.entities.length)
                    ? handleUpdateApprovalSettings()
                    : handleSaveApprovalSettings();
                }
              }}
              style="highlight"
              testSection="setup-approval-button">
              Save
            </Button>,
          ]}
          hasRequiredFieldsIndicator={true}
          onClose={closeModal}
          subtitle="You can configure whether this environment requires approvals for changes."
          testSection="setup-approval-modal"
          title={`Setup Approvals For ${truncate(environmentName, 38)}`}>
          <div className="width--11-12">
            {isLoadingEnvironmentApprovalSettings && (
              <div className="width--1-1 flex flex--dead-center">
                <Spinner />
              </div>
            )}
            {approvalSettingsError && (
              <div className="attention-wrapper">
                <Attention
                  alignment="left"
                  testSection="approvalSettingsError"
                  type="bad-news">
                  {approvalSettingsError}
                </Attention>
              </div>
            )}
            {
              <FlagsSection
                autoAddNewlyCreatedFlags={autoAddNewlyCreatedFlags}
                checkboxState={checkboxState}
                defaultRowCheckboxState={defaultRowCheckboxState}
                flagsData={flagsData}
                flagsError={flagsError as Error}
                formError={formError}
                headerCheckboxIndeterminate={headerCheckboxIndeterminate}
                headerCheckboxState={headerCheckboxState}
                initialDialogSelectedFlags={initialDialogState.selectedFlags}
                isFetchingFlags={isFetchingFlags}
                projectId={projectId}
                renderShowMoreLink={renderShowMoreLink}
                searchForFlags={searchForFlags}
                selectedFlags={selectedFlags}
                setAutoAddNewlyCreatedFlags={setAutoAddNewlyCreatedFlags}
                setCheckboxState={setCheckboxState}
                setDefaultRowCheckboxState={setDefaultRowCheckboxState}
                setFormError={setFormError}
                setHeaderCheckboxIndeterminate={setHeaderCheckboxIndeterminate}
                setHeaderCheckboxState={setHeaderCheckboxState}
                setSelectedFlags={setSelectedFlags}
                showMoreEntities={showMoreEntities}
              />
            }
            {
              <ApproversSection
                formError={formError}
                isFetchingUsers={isFetchingUsers}
                renderShowMoreLink={renderShowMoreLink}
                selectedApprovers={selectedApprovers}
                setFormError={setFormError}
                setSelectedApprovers={setSelectedApprovers}
                showMoreEntities={showMoreEntities}
                usersData={usersData}
                usersError={usersError}
              />
            }
            {
              <AdditionalUsersSection
                isFetchingUsers={isFetchingUsers}
                renderShowMoreLink={renderShowMoreLink}
                selectedAdditionalUsers={selectedAdditionalUsers}
                setSelectedAdditionalUsers={setSelectedAdditionalUsers}
                showMoreEntities={showMoreEntities}
                usersData={usersData}
                usersError={usersError}
              />
            }
          </div>
        </Sheet>
      )}
    </div>
  );
};

// Reusable Component for Empty Messages
const EmptyMessage = ({
  subtitle,
  title,
}: {
  subtitle: string;
  title: string;
}) => {
  return (
    <Fieldset className="push-double--top">
      <p className="axiom-typography--body">{title}</p>
      <p className="axiom-typography--body muted">{subtitle}</p>
    </Fieldset>
  );
};
const FlagsSection = ({
  autoAddNewlyCreatedFlags,
  setAutoAddNewlyCreatedFlags,
  flagsData,
  flagsError,
  initialDialogSelectedFlags,
  isFetchingFlags,
  formError,
  setFormError,
  searchForFlags,
  setSelectedFlags,
  selectedFlags,
  headerCheckboxState,
  setHeaderCheckboxState,
  defaultRowCheckboxState,
  setDefaultRowCheckboxState,
  checkboxState,
  setCheckboxState,
  headerCheckboxIndeterminate,
  showMoreEntities,
  setHeaderCheckboxIndeterminate,
  renderShowMoreLink,
}: {
  autoAddNewlyCreatedFlags: boolean;
  checkboxState: checkboxType;
  defaultRowCheckboxState: CheckboxState;
  flagsData: PageableAPIResponse<Flags>;
  flagsError?: Error;
  formError?: boolean;
  headerCheckboxIndeterminate: CheckboxState;
  headerCheckboxState: CheckboxState;
  initialDialogSelectedFlags: SelectedFlag[];
  isFetchingFlags: boolean;
  projectId: string;
  renderShowMoreLink: (
    selectedEntities: any[],
    entityType: 'flags' | 'approvers' | 'additionalUsers'
  ) => JSX.Element | null;
  searchForFlags: (params: { [key: string]: any }) => Promise<any>;
  selectedFlags: SelectedFlag[];
  setAutoAddNewlyCreatedFlags: Dispatch<SetStateAction<boolean>>;
  setCheckboxState: Dispatch<SetStateAction<checkboxType>>;
  setDefaultRowCheckboxState: Dispatch<SetStateAction<CheckboxState>>;
  setFormError: React.Dispatch<React.SetStateAction<boolean>>;
  setHeaderCheckboxIndeterminate: Dispatch<SetStateAction<CheckboxState>>;
  setHeaderCheckboxState: Dispatch<SetStateAction<CheckboxState>>;
  setSelectedFlags: Dispatch<SetStateAction<SelectedFlag[]>>;
  showMoreEntities: ShowMoreEntities;
}) => {
  // Toggle individual row checkboxes
  const handleCheckboxChange = (flagKey: string, id: CheckboxId) => {
    setCheckboxState(prevState => {
      const updatedCheckboxes = prevState[id].includes(flagKey)
        ? prevState[id].filter(key => key !== flagKey)
        : [...prevState[id], flagKey];
      updatedCheckboxes.sort();
      // Update selectedFlags based on checkbox changes
      const updatedSelectedFlags = selectedFlags.map(flag => {
        if (flag.key === flagKey) {
          return {
            ...flag,
            entity_id: flagsData.items.find(
              flagdata => flagdata.key === flag.key
            )?.id as number,
            preferences: {
              ...flag.preferences,
              [id]: updatedCheckboxes.includes(flagKey),
            },
          };
        }
        return flag;
      });

      setSelectedFlags(updatedSelectedFlags);
      // Check if all checkboxes in the column are selected for the header checkbox
      const allSelected =
        selectedFlags.every(flag => updatedCheckboxes.includes(flag.key)) &&
        defaultRowCheckboxState[id]; // Include default
      const atLeastOneSelected =
        selectedFlags.some(flag => updatedCheckboxes.includes(flag.key)) ||
        defaultRowCheckboxState[id];

      setHeaderCheckboxState(prevHeaderState => ({
        ...prevHeaderState,
        [id]: allSelected,
      }));
      setHeaderCheckboxIndeterminate(prevState => ({
        ...prevState,
        [id]: !allSelected && atLeastOneSelected,
      }));
      return { ...prevState, [id]: updatedCheckboxes };
    });
  };

  // Toggle header checkbox (select all / deselect all)
  const handleHeaderCheckboxChange = (id: CheckboxId) => {
    setHeaderCheckboxState(prevState => {
      const isChecked = !prevState[id];

      setCheckboxState(prevState => {
        const isSelectingAll = isChecked; // true when selecting all, false when deselecting all
        const updatedCheckboxes = isSelectingAll
          ? selectedFlags.map(flag => flag.key).sort() // Select all
          : []; // Deselect all

        // Update selectedFlags with new checkbox state
        const updatedSelectedFlags = selectedFlags.map(flag => ({
          ...flag,
          entity_id: flagsData.items.find(flagdata => flagdata.key === flag.key)
            ?.id as number,
          preferences: {
            ...flag.preferences,
            [id]: updatedCheckboxes.includes(flag.key),
          },
        }));

        setSelectedFlags(updatedSelectedFlags);

        return {
          ...prevState,
          [id]: updatedCheckboxes,
        };
      });

      // Update the default row checkbox
      setDefaultRowCheckboxState(prevState => ({
        ...prevState,
        [id]: isChecked, // Set default row to match the header state
      }));
      setHeaderCheckboxIndeterminate(prevState => ({
        ...prevState,
        [id]: false,
      }));

      return { ...prevState, [id]: isChecked };
    });
  };

  const handleDefaultCheckboxChange = (id: CheckboxId) => {
    setDefaultRowCheckboxState(prevState => {
      const isChecked = !prevState[id];

      // Check if all individual rows (flags) are selected
      const allSelected =
        selectedFlags.every(flag => checkboxState[id].includes(flag.key)) &&
        isChecked;

      const atLeastOneSelected =
        selectedFlags.some(flag => checkboxState[id].includes(flag.key)) ||
        isChecked;
      // Sync the header checkbox state based on row states
      setHeaderCheckboxState(prevHeaderState => ({
        ...prevHeaderState,
        [id]: allSelected,
      }));
      setHeaderCheckboxIndeterminate(prevState => ({
        ...prevState,
        [id]: !allSelected && atLeastOneSelected,
      }));

      return { ...prevState, [id]: isChecked };
    });
  };

  // Reusable Component for Table Row with Checkbox
  const TableCheckboxRow = ({
    id,
    label,
  }: {
    id: CheckboxId;
    label: string;
  }) => (
    <Table.TH className="soft-half--left soft-half--right">
      <Poptip
        content={
          <>
            <p className="axiom-typography--subhead text--left">
              Action(s) that Requires Approval:
            </p>
            {ApprovalActionsTooltip[id] ? (
              <ul className="lego-list lego-list--bullet text--left">
                {ApprovalActionsTooltip[id].map((action, index) => (
                  <li className="axiom-typography--body" key={index}>
                    {action}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No actions available for this section.</p>
            )}
          </>
        }
        isInline={true}
        theme={'light'}>
        {label}
        <Icon
          className="lego-icon muted vertical-align--middle cursor--pointer flush--left flex--none"
          name="circle-exclamation"
          size="small"
        />
      </Poptip>

      <Checkbox
        checked={headerCheckboxState[id]}
        className="push-half--top"
        data-test-section={`header-checkbox-${id}`}
        id={`header-checkbox-${id}`}
        indeterminate={headerCheckboxIndeterminate[id]}
        onChange={() => handleHeaderCheckboxChange(id)}
      />
    </Table.TH>
  );
  // Flags Table Component
  const FlagsTable = () => {
    const visibleFlags = showMoreEntities['flags']
      ? selectedFlags // Show all if expanded
      : selectedFlags.slice(0, maxInitialEntities);

    return (
      <Table style="rule" tableLayoutAlgorithm="auto">
        <Table.THead>
          <Table.TR>
            <Table.TH>Flags</Table.TH>
            <TableCheckboxRow id="ruleset" label="Ruleset" />
            <TableCheckboxRow id="rules" label="Rules" />
            <TableCheckboxRow id="audiences" label="Audiences" />
            <TableCheckboxRow id="ramp_perc" label="Ramp Pct" />
            <TableCheckboxRow id="metrics" label="Metrics" />
            <TableCheckboxRow id="variations" label="Variations" />
          </Table.TR>
        </Table.THead>
        <Table.TBody>
          {/* Default selection row */}
          <Table.TR>
            <Table.TD
              className="hard--right"
              data-test-section={'td-defaultRow'}
              width="200px">
              Default selection
              <HelpPopover
                behavior="hover"
                className="axiom-typography--body"
                horizontalAttachment="center"
                verticalAttachment="bottom">
                Default selection for all new flags, applied automatically
                unless individually overridden.
              </HelpPopover>
            </Table.TD>
            {[
              'ruleset',
              'rules',
              'audiences',
              'ramp_perc',
              'metrics',
              'variations',
            ].map(id => (
              <Table.TD className="soft-half--left" key={id}>
                <Checkbox
                  checked={defaultRowCheckboxState[id as CheckboxId]}
                  data-test-section={`default-row-checkbox-${id}`}
                  id={`default-row-checkbox-${id}`}
                  onChange={() => handleDefaultCheckboxChange(id as CheckboxId)}
                />
              </Table.TD>
            ))}
          </Table.TR>

          {/* Dynamically added rows for selected flags */}
          {visibleFlags.map(flag => (
            <Table.TR data-test-section={`${flag.key}-tableRow`} key={flag.key}>
              <Table.TD className="hard--right" width="200px">
                {flag.name}
              </Table.TD>
              {[
                'ruleset',
                'rules',
                'audiences',
                'ramp_perc',
                'metrics',
                'variations',
              ].map(id => (
                <Table.TD className="soft-half--left" key={id}>
                  <Checkbox
                    checked={checkboxState[id as CheckboxId].includes(flag.key)}
                    data-test-section={`row-checkbox-${flag.key}-${id}`}
                    id={`row-checkbox-${flag.key}-${id}`}
                    onChange={() =>
                      handleCheckboxChange(flag.key, id as CheckboxId)
                    }
                  />
                </Table.TD>
              ))}
              <Table.TD className="text--right">
                <ButtonIcon
                  iconFill="black"
                  iconName="trash-can"
                  onClick={() => handleFlagRemoval(flag.key)}
                  size="small"
                  style="plain"
                  testSection={`delete-row-${flag.key}`}
                />
              </Table.TD>
            </Table.TR>
          ))}
        </Table.TBody>
      </Table>
    );
  };
  const updateHeaderCheckboxStateForAddDelete = (
    updatedCheckboxState: checkboxType,
    updatedFlags: string | any[]
  ) => {
    // Update header checkbox states
    const newHeaderState = { ...headerCheckboxState };
    const newHeaderIndeterminate = { ...headerCheckboxIndeterminate };

    Object.keys(updatedCheckboxState).forEach(id => {
      const allChecked =
        updatedCheckboxState[id as CheckboxId].length === updatedFlags.length &&
        defaultRowCheckboxState[id as CheckboxId];
      const anyChecked =
        updatedCheckboxState[id as CheckboxId].length > 0 ||
        defaultRowCheckboxState[id as CheckboxId];

      newHeaderState[id as CheckboxId] = allChecked;

      newHeaderIndeterminate[id as CheckboxId] =
        anyChecked && !newHeaderState[id as CheckboxId];
    });

    setHeaderCheckboxState(newHeaderState);
    setHeaderCheckboxIndeterminate(newHeaderIndeterminate);
  };
  const handleFlagSelection = (
    selectedFlags: { id?: number; key: string; name: string }[]
  ) => {
    setSelectedFlags(prevFlags => {
      const newFlags = selectedFlags.filter(
        selectedFlag => !prevFlags.some(flag => flag.key === selectedFlag.key)
      );

      const removedFlags = prevFlags.filter(
        prevFlag =>
          !selectedFlags.some(selectedFlag => selectedFlag.key === prevFlag.key)
      );
      const getOperation = (flagKey: string): 'update' | 'add' => {
        return initialDialogSelectedFlags.some(
          initialFlag => initialFlag.key === flagKey
        )
          ? 'update'
          : 'add';
      };

      const updatedFlags = prevFlags
        .filter(flag => !removedFlags.includes(flag))
        .concat(
          newFlags
            .filter(flag => flag.id !== undefined) // Ensure flag.id is defined
            .map(flag => ({
              entity_id: flag.id as number,
              key: flag.key,
              name: flag.name,
              operation: getOperation(flag.key),
              preferences: {
                audiences: checkboxState.audiences.includes(flag.key),
                metrics: checkboxState.metrics.includes(flag.key),
                ramp_perc: checkboxState.ramp_perc.includes(flag.key),
                rules: checkboxState.rules.includes(flag.key),
                ruleset: checkboxState.ruleset.includes(flag.key),
                variations: checkboxState.variations.includes(flag.key),
              },
            }))
        );

      // Update checkbox states
      setCheckboxState(prevCheckboxState => {
        const updatedCheckboxState = { ...prevCheckboxState };

        Object.keys(headerCheckboxState).forEach(id => {
          if (
            // Add newly selected flags to the checkbox state if either the header or default row checkbox is selected
            headerCheckboxState[id as CheckboxId] ||
            defaultRowCheckboxState[id as CheckboxId]
          ) {
            updatedCheckboxState[id as CheckboxId] = [
              ...prevCheckboxState[id as CheckboxId],
              ...newFlags.map(flag => flag.key),
            ]
              .filter(key => !removedFlags.map(flag => flag.key).includes(key))
              .sort();
          } else {
            // Update the checkbox state to remove deselected flags, when the header or default row checkboxes are unchecked
            updatedCheckboxState[id as CheckboxId] = updatedCheckboxState[
              id as CheckboxId
            ]
              .filter(key => !removedFlags.map(flag => flag.key).includes(key))
              .sort();
          }
        });
        updateHeaderCheckboxStateForAddDelete(
          updatedCheckboxState,
          updatedFlags
        );
        return updatedCheckboxState;
      });

      return updatedFlags;
    });
  };

  const handleFlagRemoval = (flagKey: string) => {
    setSelectedFlags(prevFlags => {
      const flaggedForDeletion = prevFlags.map(flag =>
        flag.key === flagKey ? { ...flag, operation: 'delete' } : flag
      );

      const updatedFlags = flaggedForDeletion.filter(
        flag => flag.key !== flagKey || flag.operation !== 'delete'
      );

      // Update the checkbox state based on the remaining flags
      setCheckboxState(prevCheckboxState => {
        const updatedCheckboxState = { ...prevCheckboxState };

        Object.keys(updatedCheckboxState).forEach(id => {
          // Remove the deleted flag from the checkbox state
          updatedCheckboxState[id as CheckboxId] = updatedCheckboxState[
            id as CheckboxId
          ]
            .filter(key => key !== flagKey)
            .sort();
        });
        updateHeaderCheckboxStateForAddDelete(
          updatedCheckboxState,
          updatedFlags
        );
        return updatedCheckboxState;
      });
      if (formError && updatedFlags.length === 0 && !autoAddNewlyCreatedFlags) {
        setFormError(false);
      }
      return updatedFlags;
    });
  };
  const renderFlagsSectionContent = () => {
    if (isFetchingFlags) {
      return (
        <div className="push flex flex-justified--center">
          <Spinner size="small" />
        </div>
      );
    }

    if (flagsError) {
      return (
        <div className="error-message">
          <Attention alignment="left" type="bad-news">
            Failed to fetch flags: {flagsError.message || 'Unknown error'}
          </Attention>
        </div>
      );
    }

    return (
      <>
        <div className="push-double--bottom">
          <SearchMultiSelectDropdown
            entities={flagsData?.items || []}
            handleEntitySelection={handleFlagSelection}
            placeholder="Search for a Flag"
            searchForEntities={searchForFlags}
            selectedEntities={selectedFlags}
            type="Flag"
          />
        </div>
        <div className="push-triple--bottom">
          <Checkbox
            checked={autoAddNewlyCreatedFlags}
            data-test-section={'requires-auto-add-checkbox'}
            id={'requires-auto-add'}
            label="Automatically add newly created flags in this list within this environment"
            onChange={e => {
              setAutoAddNewlyCreatedFlags(e.target.checked);
              if (
                formError &&
                selectedFlags.length === 0 &&
                !e.target.checked
              ) {
                setFormError(false);
              }
            }}
          />
        </div>
        <FlagsTable />
        {selectedFlags.length > 0 ? (
          renderShowMoreLink(selectedFlags, 'flags')
        ) : (
          <EmptyMessage
            subtitle="Start by searching for flags. Any flags added in this section require reviewer approval before changes are implemented."
            title="No Flags added"
          />
        )}
      </>
    );
  };
  return (
    <Fieldset className="setup-approvals-dialog-fieldset" title="Flags">
      <p className="axiom-typography--body">
        Add the flags you need or want approval for. Including them on this list
        will require future approval.
      </p>
      {renderFlagsSectionContent()}
    </Fieldset>
  );
};
const ApproversSection = ({
  formError,
  setFormError,
  selectedApprovers,
  setSelectedApprovers,
  isFetchingUsers,
  usersError,
  usersData,
  renderShowMoreLink,
  showMoreEntities,
}: {
  formError: boolean;
  isFetchingUsers: boolean;
  renderShowMoreLink: (
    selectedEntities: any[],
    entityType: 'flags' | 'approvers' | 'additionalUsers'
  ) => JSX.Element | null;
  selectedApprovers: SelectedApprover[];
  setFormError: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedApprovers: Dispatch<SetStateAction<SelectedApprover[]>>;
  showMoreEntities: ShowMoreEntities;
  usersData: User[];
  usersError?: Error;
}) => {
  const ApproversTable = () => {
    const visibleApprovers = showMoreEntities['approvers']
      ? selectedApprovers // Show all if expanded
      : selectedApprovers.slice(0, maxInitialEntities);
    return (
      <Table style="rule" tableLayoutAlgorithm="auto">
        <Table.THead>
          <Table.TR>
            <Table.TH>User</Table.TH>
            <Table.TH>Environment Role</Table.TH>
            <Table.TH></Table.TH>
          </Table.TR>
        </Table.THead>
        <Table.TBody>
          {visibleApprovers.map(approver => {
            const role = usersData.find(user => user.user_id === approver.key)
              ?.role as keyof typeof roleDisplayNames;
            const email = usersData.find(user => user.user_id === approver.key)
              ?.email;
            return (
              <Table.TR
                data-test-section={`${approver.email}-approverTableRow`}
                key={approver.key}>
                <Table.TD>
                  <div>{approver.name}</div>
                  <div className="muted">{email}</div>
                </Table.TD>
                <Table.TD>{roleDisplayNames[role]}</Table.TD>
                <Table.TD className="text--right">
                  <ButtonIcon
                    iconFill="black"
                    iconName="trash-can"
                    onClick={() => handleApproverRemoval(approver.key)}
                    size="small"
                    style="plain"
                    testSection={`delete-row-${approver.key}`}
                  />
                </Table.TD>
              </Table.TR>
            );
          })}
        </Table.TBody>
      </Table>
    );
  };
  const handleApproverRemoval = (approverKey: string) => {
    setSelectedApprovers(prevApprovers => {
      const flaggedForDeletion = prevApprovers.map(approver =>
        approver.key === approverKey
          ? { ...approver, operation: 'delete' }
          : approver
      );

      const updatedApprovers = flaggedForDeletion.filter(
        approver =>
          approver.key !== approverKey || approver.operation !== 'delete'
      );
      return updatedApprovers;
    });
  };
  const handleApproverSelection = (
    selectedApprovers: {
      email?: string;
      key: string;
      name: string;
    }[]
  ) => {
    setSelectedApprovers(prevApprovers => {
      const newApprovers = selectedApprovers.filter(
        selectedApprover =>
          !prevApprovers.some(approver => approver.key === selectedApprover.key)
      );

      const removedApprovers = prevApprovers.filter(
        prevApprover =>
          !selectedApprovers.some(
            selectedApprover => selectedApprover.key === prevApprover.key
          )
      );

      const updatedApprovers = prevApprovers
        .filter(approver => !removedApprovers.includes(approver))
        .concat(newApprovers);

      if (formError && updatedApprovers.length > 0) {
        setFormError(false);
      }
      return updatedApprovers;
    });
  };
  const renderApproverSectionContent = () => {
    if (isFetchingUsers) {
      return (
        <div className="push flex flex-justified--center">
          <Spinner size="small" />
        </div>
      );
    }

    if (usersError) {
      return (
        <div className="error-message">
          <Attention alignment="left" type="bad-news">
            Failed to fetch approvers: {usersError.message || 'Unknown error'}
          </Attention>
        </div>
      );
    }

    return (
      <>
        <div className="push-triple--bottom">
          <SearchMultiSelectDropdown
            entities={transformUsersForDropdown(usersData, true)}
            handleEntitySelection={handleApproverSelection}
            placeholder="Search for an Approver"
            selectedEntities={selectedApprovers}
            type="User"
          />
        </div>
        {formError && (
          <div
            className="error-message push--bottom"
            data-test-section="approver-required-error">
            <span className="color--bad-news">
              At least one approver must be selected when flags are selected for
              approval or when the 'Automatically add newly created flags…'
              option is enabled.
            </span>
          </div>
        )}
        {selectedApprovers.length ? (
          <>
            <ApproversTable />
            {renderShowMoreLink(selectedApprovers, 'approvers')}
          </>
        ) : (
          <EmptyMessage
            subtitle="Start by searching for reviewers. Users added as reviewers can approve or reject changes made to this environment."
            title="No Approvers added"
          />
        )}
      </>
    );
  };

  return (
    <Fieldset
      className="setup-approvals-dialog-fieldset"
      isRequired={true}
      title="Approvers">
      <p className="axiom-typography--body">
        All approvers will receive notification emails regarding changes,
        approvals, and rejections.
      </p>
      {renderApproverSectionContent()}
    </Fieldset>
  );
};

const AdditionalUsersSection = ({
  selectedAdditionalUsers,
  setSelectedAdditionalUsers,
  isFetchingUsers,
  usersError,
  usersData,
  renderShowMoreLink,
  showMoreEntities,
}: {
  isFetchingUsers: boolean;
  renderShowMoreLink: (
    selectedEntities: any[],
    entityType: 'flags' | 'approvers' | 'additionalUsers'
  ) => JSX.Element | null;
  selectedAdditionalUsers: SelectedAdditionalUser[];
  setSelectedAdditionalUsers: Dispatch<
    SetStateAction<SelectedAdditionalUser[]>
  >;
  showMoreEntities: ShowMoreEntities;
  usersData: User[];
  usersError?: Error;
}) => {
  const AdditionalUserTable = () => {
    const visibleUsers = showMoreEntities['additionalUsers']
      ? selectedAdditionalUsers // Show all if expanded
      : selectedAdditionalUsers.slice(0, maxInitialEntities);
    return (
      <Table style="rule" tableLayoutAlgorithm="auto">
        <Table.THead>
          <Table.TR>
            <Table.TH>User</Table.TH>
            <Table.TH></Table.TH>
          </Table.TR>
        </Table.THead>
        <Table.TBody>
          {visibleUsers.map(visibleUser => {
            const email = usersData.find(
              user => user.user_id === visibleUser.key
            )?.email;
            return (
              <Table.TR
                data-test-section={`${visibleUser.email}-userTableRow`}
                key={visibleUser.key}>
                <Table.TD>
                  <div>{visibleUser.name}</div>
                  <div className="muted">{email}</div>
                </Table.TD>
                <Table.TD className="text--right">
                  <ButtonIcon
                    iconFill="black"
                    iconName="trash-can"
                    onClick={() => handleAdditionalUserRemoval(visibleUser.key)}
                    size="small"
                    style="plain"
                    testSection={`delete-row-${visibleUser.key}`}
                  />
                </Table.TD>
              </Table.TR>
            );
          })}
        </Table.TBody>
      </Table>
    );
  };
  const handleAdditionalUserRemoval = (userKey: string) => {
    setSelectedAdditionalUsers(prevAdditionalUsers => {
      const flaggedForDeletion = prevAdditionalUsers.map(additionalUser =>
        additionalUser.key === userKey
          ? { ...additionalUser, operation: 'delete' }
          : additionalUser
      );

      const updatedAdditionalUsers = flaggedForDeletion.filter(
        additionalUser =>
          additionalUser.key !== userKey ||
          additionalUser.operation !== 'delete'
      );
      return updatedAdditionalUsers;
    });
  };
  const handleAdditionalUserSelection = (
    selectedAdditionalUsers: {
      email?: string;
      key: string;
      name: string;
    }[]
  ) => {
    setSelectedAdditionalUsers(prevAdditionalUsers => {
      const newAdditionalUsers = selectedAdditionalUsers.filter(
        selectedUser =>
          !prevAdditionalUsers.some(
            additionalUser => additionalUser.key === selectedUser.key
          )
      );

      const removedAdditionalUsers = prevAdditionalUsers.filter(
        prevAdditionalUser =>
          !selectedAdditionalUsers.some(
            selectedUser => selectedUser.key === prevAdditionalUser.key
          )
      );

      const updatedAdditionalUsers = prevAdditionalUsers
        .filter(
          additionalUser => !removedAdditionalUsers.includes(additionalUser)
        )
        .concat(newAdditionalUsers);

      return updatedAdditionalUsers;
    });
  };
  const renderAdditionalNotificationSectionContent = () => {
    if (isFetchingUsers) {
      return (
        <div className="push flex flex-justified--center">
          <Spinner size="small" />
        </div>
      );
    }

    if (usersError) {
      return (
        <div className="error-message">
          <Attention alignment="left" type="bad-news">
            Failed to fetch users: {usersError.message || 'Unknown error'}
          </Attention>
        </div>
      );
    }

    return (
      <>
        <div className="push-triple--bottom">
          <SearchMultiSelectDropdown
            entities={transformUsersForDropdown(usersData)}
            handleEntitySelection={handleAdditionalUserSelection}
            placeholder="Search for a User"
            selectedEntities={selectedAdditionalUsers}
            type="User"
          />
        </div>
        {selectedAdditionalUsers.length ? (
          <>
            <AdditionalUserTable />
            {renderShowMoreLink(selectedAdditionalUsers, 'additionalUsers')}
          </>
        ) : (
          <EmptyMessage
            subtitle="Start by adding users to receive notifications about changes and approvals. Reviewers will continue to be notified about these updates."
            title="No user specified"
          />
        )}
      </>
    );
  };

  return (
    <Fieldset
      className="setup-approvals-dialog-fieldset"
      title="Additional Notifications">
      <p className="axiom-typography--body">
        Send additional notification emails about changes, approvals, and
        rejections to specific email addresses. Approvers will receive
        notifications by default.
      </p>
      {renderAdditionalNotificationSectionContent()}
    </Fieldset>
  );
};
export default SetupApprovalsDialog;
